import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import FaqSection from '@/components/feature/FaqSection';
import usePageMeta from '@/hooks/usePageMeta';
import { commonFaqs, navSites, siteCategories } from '@/mocks/navigation';

const principles = [
  { icon: 'ri-file-shield-2-line', title: '只收录有公开来源的站点', desc: '内容方向必须能追溯到公开渠道，无法验证来源的站点不做收录。' },
  { icon: 'ri-forbid-2-line', title: '不接受付费置顶', desc: '排序只参考公开热度、更新频率与用户反馈，不对外出售位置。' },
  { icon: 'ri-refresh-line', title: '每周复核一次', desc: '长期无法访问或内容方向变化的站点会被移出目录，避免收藏夹失效。' },
  { icon: 'ri-user-3-line', title: '编辑人工维护分类', desc: '分类归属由编辑判断，同一站点不会在多个分类里重复堆叠。' },
];

const milestones = [
  { year: '2024 年 · 起步', text: '从一份个人收藏夹开始，手写了一百多条备注，整理出第一批三十个站点。' },
  { year: '2025 年 · 分类成型', text: '把目录拆成十个领域，每个分类配一段导语，并开始每周复核一次。' },
  { year: '2026 年 · 开放收录', text: '上线收录申请入口，站点来源从个人整理扩展为编辑与用户共同维护。' },
];

const voices = [
  { quote: '以前找行业资讯要开好几个 App，现在从这个站进分类，一眼就看到同方向还有哪些站点。', name: '陈默', role: '内容运营' },
  { quote: '收录标准挺克制的，没什么乱七八糟的站点，帮我省了大量筛选的时间。', name: '林一舟', role: '自由撰稿人' },
  { quote: '分类做得细，汽车和消费维权这两个板块我几乎每周都会来翻一次。', name: '苏悦', role: '普通用户' },
];

export default function About() {
  const options = useMemo(() => {
    const base = (typeof __BASE_PATH__ === 'string' ? __BASE_PATH__ : '/').replace(/\/$/, '');
    const origin = window.location.origin;
    const url = `${origin}${base}/about`;
    return {
      title: '关于我们 - 爆料导航网的定位、内容原则与联系方式 | 爆料导航网',
      description:
        '了解爆料导航网的品牌定位、内容原则与联系方式。我们专注按领域分类整理公开资讯站点，每周复核目录，不接受付费置顶。',
      keywords: '关于爆料导航,爆料导航介绍,爆料导航内容原则,爆料导航联系方式',
      path: '/about',
      jsonLd: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'AboutPage',
            name: '关于爆料导航网',
            url,
            inLanguage: 'zh-CN',
            description: '爆料导航网的品牌介绍、内容原则与联系方式。',
          },
          {
            '@type': 'Organization',
            name: '爆料导航网',
            url: `${origin}${base}/`,
            email: 'contact@baoliao-nav.com',
            description: '专注分类整理信息入口的中文导航站。',
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${origin}${base}/` },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: url },
            ],
          },
        ],
      },
    };
  }, []);

  usePageMeta(options);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full px-4 md:px-6 lg:px-10 pt-24 md:pt-32 pb-10 bg-background-100">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-6 max-w-3xl font-heading text-3xl md:text-4xl font-black text-foreground-950">
            我们只做一件事：把信息入口整理清楚
          </h1>
          <p className="mt-5 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-700">
            爆料导航网是一个按领域分类整理公开资讯站点的导航站。我们不做内容生产，也不对事件本身下判断，只负责把同类站点归在一起，
            让每一次查找都少绕几个弯。目前目录覆盖 {siteCategories.length} 个领域、{navSites.length} 个精选站点。
          </p>
        </section>

        <section aria-labelledby="intro-title" className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-50">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20minimal%20editorial%20flat%20lay%20of%20stacked%20index%20cards%2C%20a%20desktop%20calendar%20and%20a%20cup%20of%20tea%20on%20a%20light%20wooden%20desk%2C%20soft%20morning%20light%2C%20muted%20cream%20and%20terracotta%20tones%2C%20clean%20simple%20background%2C%20high%20detail%20product%20photography%20style&width=1200&height=900&seq=baoliao-about-2026&orientation=landscape"
                alt="爆料导航网团队整理站点目录的工作场景"
                title="爆料导航网 - 分类整理网站目录"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <span className="font-label text-xs tracking-[0.2em] text-primary-600">WHO WE ARE</span>
              <h2 id="intro-title" className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                从一份收藏夹，长成一个目录
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                最开始这只是编辑自己用的一份收藏夹，里面按行业写满了备注。后来发现有同样困扰的人不少——想找一个方向的资讯，
                得在不同平台之间反复切换，收藏夹越攒越乱，真正要用的时候又找不到。
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                于是我们把它公开出来，逐个分类补导语、加标签、做复核。现在每个分类都是一个独立页面：同类站点排在一起，
                点开一个分区，就能顺着摸到更多方向相近的站点。
              </p>
              <div className="mt-7 grid grid-cols-3 gap-4">
                <div className="rounded-lg border border-background-200 bg-background-100 px-4 py-3">
                  <p className="font-heading text-xl font-bold text-primary-600">{siteCategories.length}</p>
                  <p className="font-label text-[11px] text-foreground-500">领域分类</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-100 px-4 py-3">
                  <p className="font-heading text-xl font-bold text-primary-600">{navSites.length}</p>
                  <p className="font-label text-[11px] text-foreground-500">精选站点</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-100 px-4 py-3">
                  <p className="font-heading text-xl font-bold text-primary-600">7 天</p>
                  <p className="font-label text-[11px] text-foreground-500">复核周期</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="principle-title" className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl">
            <span className="font-label text-xs tracking-[0.2em] text-primary-600">PRINCIPLES</span>
            <h2 id="principle-title" className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              内容原则
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              四条我们自己也在遵守的规则，也欢迎你监督。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {principles.map((item) => (
              <article key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800 text-xl">
                  <i className={item.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-sm font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section aria-labelledby="history-title" className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-50">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-4">
              <span className="font-label text-xs tracking-[0.2em] text-primary-600">MILESTONES</span>
              <h2 id="history-title" className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                发展历程
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                变化不算快，但每一步都落在目录本身。
              </p>
            </div>
            <div className="lg:col-span-8">
              <ol className="relative border-l border-background-300 pl-6 space-y-8">
                {milestones.map((item) => (
                  <li key={item.year} className="relative">
                    <span className="absolute -left-[31px] top-1.5 w-3 h-3 rounded-full bg-accent-500 ring-4 ring-accent-100"></span>
                    <h3 className="font-heading text-base font-bold text-foreground-950">{item.year}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section aria-labelledby="voice-title" className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl">
            <span className="font-label text-xs tracking-[0.2em] text-primary-600">VOICES</span>
            <h2 id="voice-title" className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              用户怎么说
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {voices.map((item) => (
              <figure key={item.name} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="w-8 h-8 flex items-center justify-center text-2xl text-accent-500">
                  <i className="ri-double-quotes-l" aria-hidden="true"></i>
                </span>
                <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">{item.quote}</blockquote>
                <figcaption className="mt-4 font-label text-xs text-foreground-500">
                  {item.name} · {item.role}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section aria-labelledby="contact-title" id="contact" className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-50">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-5">
                <span className="font-label text-xs tracking-[0.2em] text-primary-600">CONTACT</span>
                <h2 id="contact-title" className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  联系方式
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  无论是信息纠错、分类建议还是收录申请，都可以找到我们。工作日内一般会在一到两天回复。
                </p>
              </div>
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 text-lg">
                    <i className="ri-mail-line" aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">邮箱联系</h3>
                  <p className="mt-1 break-all text-sm text-foreground-600">contact@baoliao-nav.com</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-800 text-lg">
                    <i className="ri-add-circle-line" aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">提交收录</h3>
                  <p className="mt-1 text-sm text-foreground-600">通过收录表单申请，三到五个工作日审核。</p>
                  <Link to="/submit" className="mt-3 inline-flex items-center gap-1 font-label text-xs text-primary-600 hover:text-primary-700">
                    前往提交
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <FaqSection items={commonFaqs} heading="还想了解更多" subheading="关于站点收录与目录维护的常见疑问。" />
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}